import React, { useRef, useState, useCallback } from "react";
import Cropper, { type Area } from "react-easy-crop";
import {CREATE_IMAGE_LIBRARY} from "../modules/image-library/mutations";
import {userStore} from "../store/user";
import {organizationStore} from "../store/organization";
import {createApolloClient} from "../providers/ApolloClientFactory";


type Props = {
    onReady?: (blob: Blob) => void;
    size?: number;
    title?: string;
    allowLibraryAdd?: boolean;
    variant?: "square" | "landscape";
};

const apollo = createApolloClient();
const getUser = userStore.getState;
const getOrg = organizationStore.getState;

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

function dataURLToBlob(dataUrl: string): Blob {
    const [header, data] = dataUrl.split(",");
    const mimeMatch = header.match(/data:(.*?);base64/);
    const mime = mimeMatch?.[1] ?? "image/png";
    const bin = atob(data);
    const len = bin.length;
    const u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
    return new Blob([u8], { type: mime });
}

async function blobToDataURL(blob: Blob): Promise<string> {
    return await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = reject;
        fr.readAsDataURL(blob);
    });
}

function dataUrlToRawBase64(dataUrl: string): string {
    const [, raw = ""] = dataUrl.split(",", 2);
    return raw;
}

async function canvasToBlobSafe(
    canvas: HTMLCanvasElement,
    type: string,
    quality?: number
): Promise<Blob> {
    const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), type, quality)
    );
    if (blob) return blob;
    return dataURLToBlob(canvas.toDataURL(type, quality));
}

async function getCroppedThumb(
    imageSrc: string,
    pixelCrop: Area,
    outSize = 100,
    aspectRatio = 1
): Promise<{ blob: Blob; mime: string }> {
    const image = await loadImage(imageSrc);

    const canvas = document.createElement("canvas");
    canvas.width = outSize;
    canvas.height = Math.round(outSize / aspectRatio);

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable.");

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const { x, y, width, height } = pixelCrop;
    ctx.drawImage(image, x, y, width, height, 0, 0, canvas.width, canvas.height);

    try {
        const webp = await canvasToBlobSafe(canvas, "image/webp", 0.9);
        return { blob: webp, mime: "image/webp" };
    } catch {
        const png = await canvasToBlobSafe(canvas, "image/png", 0.92);
        return { blob: png, mime: "image/png" };
    }
}

const CompactThumbUploader: React.FC<Props> = ({
                                                   onReady,
                                                   size = 200,
                                                   title = "Upload Thumbnail",
                                                   allowLibraryAdd = true,
                                                   variant = "square",
                                               }) => {
    const aspectRatio = variant === "landscape" ? 16 / 9 : 1;
    const outputSize = variant === "landscape" ? size * 2 : size;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState<number>(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    const [addToLibrary, setAddToLibrary] = useState(false);
    const [displayName, setDisplayName] = useState("");
    const [caption, setCaption] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        if (!file) return;
        if (!file.type.startsWith("image/")) return;
        const fr = new FileReader();
        fr.onload = () => {
            const result = fr.result as string;
            setImageSrc(result);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setOpen(true);
            setAddToLibrary(false);
            setDisplayName("");
            setCaption("");
            setError(null);
        };
        fr.readAsDataURL(file);
        e.target.value = "";
    };

    const onComplete = useCallback((_: Area, pixels: Area) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const save = async () => {
        try {
            setError(null);
            if (!imageSrc || !croppedAreaPixels) return;

            setSaving(true);

            const { blob } = await getCroppedThumb(imageSrc, croppedAreaPixels, outputSize, aspectRatio);

            onReady?.(blob);

            if (allowLibraryAdd && addToLibrary) {
                if (!displayName.trim()) {
                    setError("Display Name is required to add to the library.");
                    setSaving(false);
                    return;
                }
                const dataUrl = await blobToDataURL(blob);
                const rawBase64 = dataUrlToRawBase64(dataUrl);

                const user = getUser().user;
                const orgId = getOrg().organizationId;

                await apollo.mutate({
                    mutation: CREATE_IMAGE_LIBRARY,
                    variables: {
                        input: {
                            display_name: displayName.trim(),
                            caption: caption || null,
                            image_base64: rawBase64,
                            is_active: true,
                            organization_id: orgId || 0,
                            last_updated_by: user?.id ?? null,
                        },
                    },
                });
            }

            setOpen(false);
            setImageSrc(null);
        } catch (e: any) {
            console.error("Save/crop error:", e);
            setError(e?.message || "Failed to save image.");
        } finally {
            setSaving(false);
        }
    };

    const isSaveDisabled =
        saving || (allowLibraryAdd && addToLibrary && !displayName.trim());

    return (
        <>
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                title={title}
                style={{
                    marginLeft: 8,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    cursor: "pointer",
                }}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M12 16V4m0 0l-4 4m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={onFileChange}
                style={{ display: "none" }}
            />

            {open && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                        padding: 16,
                    }}
                >
                    <div
                        style={{
                            width: "min(90vw,720px)",
                            height: "min(80vh,640px)",
                            background: "#fff",
                            borderRadius: 16,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            style={{
                                padding: "12px 16px",
                                borderBottom: "1px solid #e2e8f0",
                                fontWeight: 600,
                            }}
                        >
                            Crop Your Image Below:
                        </div>

                        <div style={{ position: "relative", flex: 1, background: "#111" }}>
                            <Cropper
                                image={imageSrc ?? undefined}
                                crop={crop}
                                zoom={zoom}
                                minZoom={0.3}
                                aspect={aspectRatio}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onComplete}
                                restrictPosition={false}
                                showGrid={false}
                                objectFit="contain"
                            />
                        </div>
                        {allowLibraryAdd && (
                            <div style={{ padding: 12, borderTop: "1px solid #e2e8f0" }}>
                                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 sm:left-6"
                                        checked={addToLibrary}
                                        onChange={(e) => setAddToLibrary(e.target.checked)}
                                    />
                                    <span style={{ fontWeight: 600 }}>Add to Library</span>
                                </label>

                                {addToLibrary && (
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: 12,
                                            marginTop: 12,
                                        }}
                                    >
                                        <div style={{ gridColumn: "1 / -1" }}>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Display Name <span style={{ color: "#EF4444" }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={displayName}
                                                onChange={(e) => setDisplayName(e.target.value)}
                                                required
                                                style={{
                                                    width: "100%",
                                                    border: "1px solid #e2e8f0",
                                                    borderRadius: 6,
                                                    padding: "8px 10px",
                                                    outline: "none",
                                                }}
                                            />
                                        </div>

                                        <div style={{ gridColumn: "1 / -1" }}>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Caption (optional)
                                            </label>
                                            <input
                                                type="text"
                                                value={caption}
                                                onChange={(e) => setCaption(e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    border: "1px solid #e2e8f0",
                                                    borderRadius: 6,
                                                    padding: "8px 10px",
                                                    outline: "none",
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {error && (
                                    <div
                                        style={{
                                            marginTop: 8,
                                            color: "#B91C1C",
                                            fontSize: 12,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {error}
                                    </div>
                                )}
                            </div>
                        )}

                        <div
                            style={{
                                padding: 12,
                                borderTop: "1px solid #e2e8f0",
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <label style={{ fontSize: 12, color: "#64748b" }}>Zoom</label>
                                <input
                                    style={{ accentColor: "#f04345" }}
                                    type="range"
                                    min={0.3}
                                    max={3}
                                    step={0.001}
                                    value={zoom}
                                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                                />
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOpen(false);
                                        setImageSrc(null);
                                    }}
                                    disabled={saving}
                                    style={{
                                        padding: "8px 12px",
                                        borderRadius: 5,
                                        border: "1px solid #e2e8f0",
                                        opacity: saving ? 0.7 : 1,
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={isSaveDisabled}
                                    style={{
                                        padding: "8px 12px",
                                        borderRadius: 5,
                                        border: "1px solid #B71C1C",
                                        background: isSaveDisabled ? "#fca5a5" : "#f04345",
                                        color: "#fff",
                                        fontWeight: 600,
                                        cursor: isSaveDisabled ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {saving ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CompactThumbUploader;
